import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Flex, Typography, Button, Divider } from 'antd';
import { useAuth } from '../../../context/AuthContext';
import { routesPath } from '../../constants/routesPath';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './UserIconDropdown.module.scss';
import icon from '../../styles/Icon.module.scss';
import { useCredits } from '../../../context/CreditsContext';
import { colors, fontSize, primitiveColors } from '../../../theming';
import Icon from '../icons/Icon';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { StopImpersonationButton } from '../../../components/impersonation/StopImpersonationButton';

function UserIconDropdown() {
    const { t } = useTranslation(['sidebar', 'leads', 'users', 'businesses', 'teams', 'menu']);
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { credits } = useCredits();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    // read-only impersonation: no purchases, and "Log out" stops the impersonation instead
    const { isImpersonating } = useImpersonation();

    const location = useLocation();

    const items = [
        { key: 'my-reports', route: routesPath.ROOT.DASHBOARD, iconName: 'myReports' },
        { key: 'settings', route: routesPath.ROOT.USER_SETTINGS, iconName: 'settings' },
        { key: 'transactions', route: routesPath.ROOT.TRANSACTIONS, iconName: 'transactions' },
    ].map(({ key, route, iconName }) => {
        const isActive = location.pathname === route;
        const color = isActive ? primitiveColors.main : primitiveColors.mainText;

        return {
            key: t(`sidebar:${key}`),
            route,
            title: <span style={{ color }}>{t(`sidebar:${key}`)}</span>,
            icon: <Icon color={color} name={iconName} />,
            label: (
                <span className={classes.menuItemNav} style={{ color }}>
                    {t(`sidebar:${key}`)}
                </span>
            ),
        };
    });

    const onMenuClick = ({ key }) => {
        const menuItem = items.find(item => item.key === key);
        if (menuItem?.route && location.pathname !== menuItem.route) {
            navigate(menuItem.route);
        }
    };

    const menuItems = [
        {
            key: 'your-credits',
            label: (
                <Flex justify="space-between" align="center">
                    <Typography.Text style={{ fontWeight: 500, color: primitiveColors.text, fontSize: fontSize.sm }}>
                        {t('sidebar:my-credits')}:
                    </Typography.Text>

                    <div className={classes.creditsValue}>{credits || 0}</div>
                </Flex>
            ),
        },
        { key: 'divider1', label: <Divider /> },
        {
            key: 'get-more-credits',
            label: (
                <Flex justify="center">
                    <Button
                        className={classes.getMoreCredits}
                        type="primary"
                        size="large"
                        disabled={isImpersonating}
                        onClick={() => navigate(routesPath.ROOT.PAYMENT)}
                    >
                        {t('sidebar:get-button')}
                    </Button>
                </Flex>
            ),
        },
        ...items,
        { key: 'divider2', label: <Divider /> },
        {
            key: 'logout',
            label: isImpersonating ? (
                <StopImpersonationButton
                    type="primary"
                    size="large"
                    className={classes.logoutButton}
                    renderLabel={label => (
                        <>
                            <i className={`fi fi-sr-exit ${classes.logoutIcon}`} />
                            <span className={classes.logout}>{label}</span>
                        </>
                    )}
                />
            ) : (
                <Button type="primary" size="large" onClick={logout} className={classes.logoutButton}>
                    <i className={`fi fi-sr-exit ${classes.logoutIcon}`} />
                    <span className={classes.logout}>{t('menu:log-out')}</span>
                </Button>
            ),
        },
    ];

    return (
        <Dropdown
            menu={{ items: menuItems, onClick: onMenuClick }}
            placement="bottomLeft"
            overlayClassName={classes['user-icon-dropdown-menu']}
            trigger={['click']}
            onOpenChange={setIsDropdownOpen}
        >
            <Button
                className={classes.buttonMyAccount}
                style={{
                    color: isDropdownOpen ? colors.white : primitiveColors.text,
                    backgroundColor: isDropdownOpen ? primitiveColors.text : 'transparent',
                    border: `1px solid ${primitiveColors.text}`,
                }}
            >
                <i
                    className={`fi fi-rr-user ${icon.md}`}
                    style={{ marginTop: 0, color: isDropdownOpen ? colors.white : primitiveColors.text }}
                ></i>
                {t('sidebar:my-account')}

                <i
                    className={`fi fi-rr-caret-down ${icon.md}`}
                    style={{
                        marginTop: 0,
                        color: isDropdownOpen ? colors.white : primitiveColors.text,
                        transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s ease-in-out',
                    }}
                ></i>
            </Button>
        </Dropdown>
    );
}

export default UserIconDropdown;
